import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box, IconButton, Paper, Tooltip } from "@mui/material";
import { CanvasView, GRID_CONSTANTS } from "store/types";

const { GAP, GRID_CELL_HEIGHT } = GRID_CONSTANTS;

interface CanvasCardProps {
  card: CanvasView;
  gridCellWidth: number;
  isDragging: boolean;
  onDragStart: (card: CanvasView, e: React.MouseEvent) => void;
  onResizeStart: (card: CanvasView, e: React.MouseEvent) => void;
  onOpenCustomization: (card: CanvasView) => void;
  onRemove: (cardId: string) => void;
  getCardWidth: (span: number) => number;
  getCardHeight: (rowSpan: number) => number;
  children: React.ReactNode;
}

export const CanvasCard: React.FC<CanvasCardProps> = ({
  card,
  gridCellWidth,
  isDragging,
  onDragStart,
  onResizeStart,
  onOpenCustomization,
  onRemove,
  getCardWidth,
  getCardHeight,
  children
}) => {
  return (
    <Paper
      elevation={isDragging ? 6 : 2}
      onMouseDown={(e) => onDragStart(card, e)}
      sx={{
        position: "absolute",
        left: card.gridCol * (gridCellWidth + GAP),
        top: card.gridRow * (GRID_CELL_HEIGHT + GAP),
        width: getCardWidth(card.gridSpan),
        height: getCardHeight(card.gridRowSpan || 2),
        overflow: "hidden",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        cursor: isDragging ? "grabbing" : "grab",
        opacity: isDragging ? 0.7 : 1,
        transition: isDragging ? "none" : "box-shadow 0.2s, opacity 0.2s",
        zIndex: isDragging ? 100 : 1,
        "&:hover": {
          boxShadow: 4,
          "& .card-actions": {
            opacity: 1
          }
        }
      }}
    >
      <Box
        className="card-actions"
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          display: "flex",
          gap: 0.5,
          opacity: 0,
          transition: "opacity 0.2s",
          zIndex: 10
        }}
      >
        <Tooltip title="Settings">
          <IconButton
            size="sm"
            onClick={() => onOpenCustomization(card)}
            sx={{ bgcolor: "white", "&:hover": { bgcolor: "#f0f0f0" } }}
          >
            <SettingsIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove">
          <IconButton
            size="sm"
            onClick={() => onRemove(card.id)}
            sx={{ bgcolor: "white", "&:hover": { bgcolor: "#fee2e2" } }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ height: "100%", overflow: "auto" }}>{children}</Box>
      <Box
        className="card-actions"
        onMouseDown={(e) => onResizeStart(card, e)}
        sx={{
          position: "absolute",
          bottom: 4,
          right: 4,
          width: 20,
          height: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "nwse-resize",
          opacity: 0,
          transition: "opacity 0.2s",
          bgcolor: "white",
          borderRadius: "4px",
          border: "1px solid #e2e8f0",
          "&:hover": {
            bgcolor: "#f0f0f0"
          }
        }}
      >
        <OpenInFullIcon sx={{ fontSize: 12, color: "#64748b", transform: "rotate(90deg)" }} />
      </Box>
    </Paper>
  );
};
